// ---------------------------------------------------------------------------
// AgentsService — HTTP-backed implementation against WorkflowSvc.
//
// Talks to /workspaces/{workspaceName}/evaluation/agents. Covers the full
// working-copy CRUD surface, snapshot creation (POST :snapshot), and read-only
// version history.
// ---------------------------------------------------------------------------

import type { IHttpClient } from '../api/client.js';
import { AppError } from '../api/errors.js';
import type {
  Agent,
  AgentVersion,
  PatchAgentBody,
  UpsertAgentBody,
} from '../types/agent.js';
import type { CollectionResponse } from '../types/common.js';

import type { IAgentsService } from './agentsService.interface.js';
import {
  AgentAlreadyExistsError,
  AgentNotFoundError,
  type AgentsRequestOptions,
} from './agentsService.interface.js';

function basePath(workspaceName: string): string {
  return `workspaces/${encodeURIComponent(workspaceName)}/evaluation/agents`;
}

function agentPath(workspaceName: string, name: string): string {
  return `${basePath(workspaceName)}/${encodeURIComponent(name)}`;
}

function isHttpStatus(err: unknown, status: number): boolean {
  return err instanceof AppError && err.statusCode === status;
}

export class AgentsService implements IAgentsService {
  constructor(private readonly client: IHttpClient) {}

  async list(
    workspaceName: string,
    options?: AgentsRequestOptions,
  ): Promise<Agent[]> {
    const { data } = await this.client.get<CollectionResponse<Agent>>(
      basePath(workspaceName),
      { signal: options?.signal },
    );
    return data.value;
  }

  async get(
    workspaceName: string,
    name: string,
    options?: AgentsRequestOptions,
  ): Promise<Agent> {
    try {
      const { data } = await this.client.get<Agent>(
        agentPath(workspaceName, name),
        { signal: options?.signal },
      );
      return data;
    } catch (err) {
      if (isHttpStatus(err, 404)) {
        throw new AgentNotFoundError(workspaceName, name);
      }
      throw err;
    }
  }

  async create(
    workspaceName: string,
    name: string,
    body: UpsertAgentBody,
    options?: AgentsRequestOptions,
  ): Promise<Agent> {
    try {
      const { data } = await this.client.put<Agent>(
        agentPath(workspaceName, name),
        body,
        { headers: { 'If-None-Match': '*' }, signal: options?.signal },
      );
      return data;
    } catch (err) {
      if (isHttpStatus(err, 409) || isHttpStatus(err, 412)) {
        throw new AgentAlreadyExistsError(workspaceName, name);
      }
      throw err;
    }
  }

  async replace(
    workspaceName: string,
    name: string,
    body: UpsertAgentBody,
    options?: AgentsRequestOptions,
  ): Promise<Agent> {
    const { data } = await this.client.put<Agent>(
      agentPath(workspaceName, name),
      body,
      { signal: options?.signal },
    );
    return data;
  }

  async patch(
    workspaceName: string,
    name: string,
    body: PatchAgentBody,
    options?: AgentsRequestOptions,
  ): Promise<Agent> {
    try {
      const { data } = await this.client.patch<Agent>(
        agentPath(workspaceName, name),
        body,
        { signal: options?.signal },
      );
      return data;
    } catch (err) {
      if (isHttpStatus(err, 404)) {
        throw new AgentNotFoundError(workspaceName, name);
      }
      throw err;
    }
  }

  async delete(
    workspaceName: string,
    name: string,
    options?: AgentsRequestOptions,
  ): Promise<void> {
    try {
      await this.client.delete<void>(
        agentPath(workspaceName, name),
        undefined,
        {
          signal: options?.signal,
        },
      );
    } catch (err) {
      if (isHttpStatus(err, 404)) {
        throw new AgentNotFoundError(workspaceName, name);
      }
      throw err;
    }
  }

  async snapshot(
    workspaceName: string,
    name: string,
    options?: AgentsRequestOptions,
  ): Promise<AgentVersion> {
    try {
      const { data } = await this.client.post<AgentVersion>(
        `${agentPath(workspaceName, name)}:snapshot`,
        undefined,
        { signal: options?.signal },
      );
      return data;
    } catch (err) {
      if (isHttpStatus(err, 404)) {
        throw new AgentNotFoundError(workspaceName, name);
      }
      throw err;
    }
  }

  async listVersions(
    workspaceName: string,
    name: string,
    options?: AgentsRequestOptions,
  ): Promise<AgentVersion[]> {
    try {
      const { data } = await this.client.get<CollectionResponse<AgentVersion>>(
        `${agentPath(workspaceName, name)}/versions`,
        { signal: options?.signal },
      );
      return data.value;
    } catch (err) {
      if (isHttpStatus(err, 404)) {
        throw new AgentNotFoundError(workspaceName, name);
      }
      throw err;
    }
  }

  async getVersion(
    workspaceName: string,
    name: string,
    version: number,
    options?: AgentsRequestOptions,
  ): Promise<AgentVersion> {
    try {
      const { data } = await this.client.get<AgentVersion>(
        `${agentPath(workspaceName, name)}/versions/${version}`,
        { signal: options?.signal },
      );
      return data;
    } catch (err) {
      if (isHttpStatus(err, 404)) {
        throw new AgentNotFoundError(workspaceName, name);
      }
      throw err;
    }
  }
}
